import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { salesOrderQueryKeys } from '@/lib/services/sales-orders/sales-orders.query'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  checkCondolencesToken,
  connectCondolencesWithLogin,
  getCondolencesIntegration,
  refreshCondolencesOrders,
  updateCondolencesIntegration,
  type CondolencesQueryKeyEntries,
} from './condolences.api'
import type {
  CondolencesIntegration,
  CondolencesIntegrationInput,
  CondolencesLoginInput,
} from './condolences.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries the fetcher reads; the scoped key builder below sets them. */
type CondolencesIntegrationQueryKey = [AdminQueryRoot & CondolencesQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const condolencesQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'condolences' }, { organizationId }),
  ],
  integration: ({ organizationId }: OrganizationScope): CondolencesIntegrationQueryKey =>
    buildScopedAdminQueryKey(condolencesQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'integration',
    }),
}

export const condolencesQueries = {
  integration: ({ organizationId }: OrganizationScope) =>
    queryOptions({
      queryKey: condolencesQueryKeys.integration({ organizationId }),
      queryFn: getCondolencesIntegration,
    }),
}

type CondolencesIntegrationParams = OrganizationScope &
  QueryParams<ReturnType<typeof condolencesQueries.integration>>

/** A failed Condolences call may have marked the connection for reconnecting, so the page re-reads it. */
function refetchCondolencesIntegration(organizationId: number) {
  return queryClient.invalidateQueries({
    queryKey: condolencesQueryKeys.integration({ organizationId }),
  })
}

function syncCondolencesIntegrationCaches(
  organizationId: number,
  integration: CondolencesIntegration
) {
  queryClient.setQueryData(condolencesQueryKeys.integration({ organizationId }), integration)

  return queryClient.invalidateQueries({
    queryKey: condolencesQueryKeys.all({ organizationId }),
  })
}

export function useCondolencesIntegration({
  organizationId,
  ...params
}: CondolencesIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...condolencesQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useSuspenseCondolencesIntegration({
  organizationId,
  ...params
}: CondolencesIntegrationParams = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...condolencesQueries.integration({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUpdateCondolencesIntegrationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CondolencesIntegrationInput) =>
      updateCondolencesIntegration({ organizationId, ...values }),
    onSuccess: (integration) => syncCondolencesIntegrationCaches(organizationId, integration),
  })
}

export function useConnectCondolencesLoginMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ email, password }: CondolencesLoginInput) =>
      connectCondolencesWithLogin({ organizationId, email, password }),
    onSuccess: (integration) => syncCondolencesIntegrationCaches(organizationId, integration),
  })
}

export function useCheckCondolencesTokenMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => checkCondolencesToken({ organizationId }),
    onSuccess: (response) => syncCondolencesIntegrationCaches(organizationId, response.integration),
    onError: () => refetchCondolencesIntegration(organizationId),
  })
}

export function useRefreshCondolencesOrdersMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: () => refreshCondolencesOrders({ organizationId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: condolencesQueryKeys.all({ organizationId }),
      })
      return queryClient.invalidateQueries({
        queryKey: salesOrderQueryKeys.all({ organizationId }),
      })
    },
    onError: () => refetchCondolencesIntegration(organizationId),
  })
}
